import { useState, useEffect, useCallback } from 'react';
import { ReturnEligibilityResponse, ReturnFormData } from '../../../../types/sales.types';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';

export interface UseReturnOrderModalProps {
  open: boolean;
  eligibility: ReturnEligibilityResponse | null;
  onSubmit: (data: ReturnFormData) => void;
}

const DEFAULT_FORM_DATA: ReturnFormData = {
  returnReason: 'CUSTOMER_CHANGED_MIND',
  returnNotes: '',
  itemCondition: 'NEW',
  applyRestockingFee: false,
  restockingFeePercentage: 0,
  refundReference: '',
  refundNotes: '',
  customRefundAmount: undefined,
};

export const useReturnOrderModal = ({ open, eligibility, onSubmit }: UseReturnOrderModalProps) => {
  const [formData, setFormData] = useState<ReturnFormData>(DEFAULT_FORM_DATA);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState<number>(0);

  // Get refundable amount from eligibility
  const refundableAmount = eligibility?.refundableAmount ?? 0;

  // Reset form when modal opens: full refundable amount, custom amount off by default
  useEffect(() => {
    if (open) {
      setUseCustomAmount(false);
      setCustomAmount(refundableAmount);
      setFormData((prev) => ({
        ...prev,
        customRefundAmount: undefined,
      }));
    }
  }, [open, refundableAmount]);

  // Calculate adjustment (difference between original and custom)
  const calculateAdjustment = useCallback(() => {
    if (useCustomAmount && customAmount > 0) {
      return refundableAmount - customAmount;
    }
    return 0;
  }, [useCustomAmount, customAmount, refundableAmount]);

  // Validate custom amount
  const isCustomAmountValid = useCallback(() => {
    if (!useCustomAmount) return true;
    return customAmount > 0 && customAmount <= refundableAmount;
  }, [useCustomAmount, customAmount, refundableAmount]);

  // Handle custom amount toggle
  const handleCustomAmountToggle = useCallback(
    (checked: boolean) => {
      setUseCustomAmount(checked);
      if (checked) {
        setCustomAmount(refundableAmount);
        setFormData((prev) => ({
          ...prev,
          customRefundAmount: refundableAmount,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          customRefundAmount: undefined,
        }));
      }
    },
    [refundableAmount],
  );

  // Handle custom amount change
  const handleCustomAmountChange = useCallback((value: string) => {
    const numValue = value === '' ? 0 : Number(value);
    setCustomAmount(numValue);
    setFormData((prev) => ({
      ...prev,
      customRefundAmount: numValue > 0 ? numValue : undefined,
    }));
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, returnNotes: value }));
  }, []);

  // Handle submit
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Validate before submit
      if (!isCustomAmountValid()) {
        return;
      }
      // Send form data - backend will handle validation
      onSubmit(formData);
    },
    [formData, isCustomAmountValid, onSubmit],
  );

  // Calculated values
  const adjustment = calculateAdjustment();
  const finalRefundAmount = useCustomAmount ? customAmount : refundableAmount;

  // Format currency (Bolivianos)
  const formatCurrency = (amount: number) => {
    return formatCurrencyValue(amount);
  };

  return {
    formData,
    useCustomAmount,
    customAmount,
    refundableAmount,
    adjustment,
    finalRefundAmount,
    isValid: isCustomAmountValid(),
    handleCustomAmountToggle,
    handleCustomAmountChange,
    handleNotesChange,
    handleSubmit,
    formatCurrency,
  };
};
